import { useContext } from 'react'
import cx from 'classnames'

import {
  OrderFinancialStatus,
  OrderFulfillmentStatus,
} from '@data/shopify/storefront/types'
import { type SanityAccountOrderListStrings } from '@data/sanity/queries/types/blocks'
import { getPrice } from '@lib/helpers'
import { LanguageContext } from '@lib/language-context'
import { ShopContext } from '@lib/shop-context'
import { StringsContext } from '@lib/strings-context'
import { useUserOrderList } from '@lib/user'

import Button, { ButtonSize, ButtonVariant } from '@components/buttons/button'
import ComplexPortableText from '@components/complex-portable-text'

interface AccountOrderListProps {
  className?: string
  accountOrderListStrings: SanityAccountOrderListStrings
}

type PaymentStatusValues<T> = {
  [P in OrderFinancialStatus]: T
}

type FulfillmentStatusValues<T> = {
  [P in OrderFulfillmentStatus]: T
}

/**
 * Gets order payment status texts.
 */
const getPaymentStatuses = (
  accountOrderListStrings: SanityAccountOrderListStrings,
): PaymentStatusValues<string> => ({
  [OrderFinancialStatus.Authorized]:
    accountOrderListStrings.accountOrderListPaymentStatusAuthorized,
  [OrderFinancialStatus.Paid]:
    accountOrderListStrings.accountOrderListPaymentStatusPaid,
  [OrderFinancialStatus.PartiallyPaid]:
    accountOrderListStrings.accountOrderListPaymentStatusPartiallyPaid,
  [OrderFinancialStatus.PartiallyRefunded]:
    accountOrderListStrings.accountOrderListPaymentStatusPartiallyRefunded,
  [OrderFinancialStatus.Pending]:
    accountOrderListStrings.accountOrderListPaymentStatusPending,
  [OrderFinancialStatus.Refunded]:
    accountOrderListStrings.accountOrderListPaymentStatusRefunded,
  [OrderFinancialStatus.Voided]:
    accountOrderListStrings.accountOrderListPaymentStatusVoided,
})

/**
 * Gets order fulfillment status texts.
 */
const getFulfillmentStatuses = (
  accountOrderListStrings: SanityAccountOrderListStrings,
): FulfillmentStatusValues<string> => ({
  [OrderFulfillmentStatus.Fulfilled]:
    accountOrderListStrings.accountOrderListFulfillmentStatusFulfilled,
  [OrderFulfillmentStatus.InProgress]:
    accountOrderListStrings.accountOrderListFulfillmentStatusInProgress,
  [OrderFulfillmentStatus.OnHold]:
    accountOrderListStrings.accountOrderListFulfillmentStatusOnHold,
  [OrderFulfillmentStatus.Open]:
    accountOrderListStrings.accountOrderListFulfillmentStatusOpen,
  [OrderFulfillmentStatus.PartiallyFulfilled]:
    accountOrderListStrings.accountOrderListFulfillmentStatusPartiallyFulfilled,
  [OrderFulfillmentStatus.PendingFulfillment]:
    accountOrderListStrings.accountOrderListFulfillmentStatusPendingFulfillment,
  [OrderFulfillmentStatus.Restocked]:
    accountOrderListStrings.accountOrderListFulfillmentStatusRestocked,
  [OrderFulfillmentStatus.Scheduled]:
    accountOrderListStrings.accountOrderListFulfillmentStatusScheduled,
  [OrderFulfillmentStatus.Unfulfilled]:
    accountOrderListStrings.accountOrderListFulfillmentStatusUnfulfilled,
})

const AccountOrderList = ({
  accountOrderListStrings,
  className,
}: AccountOrderListProps) => {
  const { currency, taxRate } = useContext(ShopContext)
  const { locale } = useContext(LanguageContext)
  const strings = useContext(StringsContext)
  const { orders, hasMoreOrders, loadOrders, isLoadingOrders } =
    useUserOrderList()

  const paymentStatuses = getPaymentStatuses(accountOrderListStrings)
  const fulfillmentStatuses = getFulfillmentStatuses(accountOrderListStrings)

  const dateFormat = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  return (
    <div className={cx('space-y-6', className)}>
      <h2>{accountOrderListStrings.accountOrderListHeading}</h2>

      {orders.length === 0 && !isLoadingOrders && (
        <ComplexPortableText
          className="rc"
          content={accountOrderListStrings.accountOrderListEmpty}
        />
      )}

      {orders.length > 0 && (
        <div>
          <div className="hidden sm:flex justify-evenly gap-x-4 py-5 px-3 text-xs uppercase text-pageText bg-gray-light border-t border-gray">
            <div className="w-1/4">
              {accountOrderListStrings.accountOrderListOrder}
            </div>
            <div className="w-1/4">
              {accountOrderListStrings.accountOrderListDate}
            </div>
            <div className="w-1/4">
              {accountOrderListStrings.accountOrderListPaymentStatus}
            </div>
            <div className="w-1/4">
              {accountOrderListStrings.accountOrderListFulfillmentStatus}
            </div>
            <div className="w-1/4 text-right">
              {accountOrderListStrings.accountOrderListTotal}
            </div>
          </div>

          {orders.map((order) => (
            <a
              key={order.id}
              href={order.url}
              target="_blank"
              rel="noreferrer"
              className="flex flex-col sm:flex-row cursor-pointer gap-x-4 gap-y-2 py-5 px-3 text-sm border-t border-gray"
            >
              <div className="sm:w-1/4 flex justify-between sm:block">
                <div className="sm:hidden">
                  {accountOrderListStrings.accountOrderListOrder}
                </div>

                <div className="text-right sm:text-left">{order.id}</div>
              </div>

              <div className="sm:w-1/4 flex justify-between sm:block">
                <div className="sm:hidden">
                  {accountOrderListStrings.accountOrderListDate}
                </div>

                <div className="text-right sm:text-left">
                  {dateFormat.format(new Date(order.date))}
                </div>
              </div>

              <div className="sm:w-1/4 flex justify-between sm:block">
                <div className="sm:hidden">
                  {accountOrderListStrings.accountOrderListPaymentStatus}
                </div>

                <div className="text-right sm:text-left">
                  {order.paymentStatus
                    ? paymentStatuses[order.paymentStatus]
                    : '-'}
                </div>
              </div>

              <div className="sm:w-1/4 flex justify-between sm:block">
                <div className="sm:hidden">
                  {accountOrderListStrings.accountOrderListFulfillmentStatus}
                </div>

                <div className="text-right sm:text-left">
                  {fulfillmentStatuses[order.fulfillmentStatus]}
                </div>
              </div>

              <div className="sm:w-1/4 flex justify-between sm:block">
                <div className="sm:hidden">
                  {accountOrderListStrings.accountOrderListTotal}
                </div>

                <div className="text-right">
                  {currency}
                  {getPrice(order.total * 100, taxRate)}
                </div>
              </div>
            </a>
          ))}

          {hasMoreOrders && (
            <Button
              variant={ButtonVariant.OUTLINED}
              disabled={isLoadingOrders}
              className="w-full"
              onClick={loadOrders}
            >
              {isLoadingOrders ? strings.buttonLoading : strings.buttonLoadMore}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

export default AccountOrderList
